import { ChoiceType, FieldType } from "#gen/forms/v1/field_pb.js";
import type { Schema } from "#lib/components/jsonForms/jsonSchema.js";
import { escapeRegex, unescapeRegex } from "#lib/regexp.js";

export enum OperandType {
	TEXT = "text",
	NUMBER = "number",
	EMPTY = "empty",
	REGEX = "regex",
	CHOICE = "choice",
}

export interface OperatorOptions {
	fieldType?: FieldType | ((_: FieldType) => boolean);
	operandType: OperandType | ((_?: FieldType) => OperandType);
	choiceTypes?: ChoiceType[];
	getValue?: (s: Schema) => string | number | undefined;
	getSchema: SchemaProducer;
}

function schemaConst(schema?: Schema): string | number | undefined {
	const value = schema?.const;
	return typeof value === "string" || typeof value === "number" ? value : undefined;
}

function schemaFromContains(contains: Schema["contains"]): Schema | undefined {
	return typeof contains === "object" && (contains as Schema | null) !== null ? contains : undefined;
}

export const operators = {
	equals: {
		operandType: f => (f === FieldType.NUMBER ? OperandType.NUMBER : OperandType.TEXT),
		fieldType: undefined,
		getValue: (s: Schema) => schemaConst(s),
		getSchema: (key, value) =>
			({
				type: "object",
				description: "equals",
				required: [key],
				properties: {
					[key]: {
						const: value,
					},
				},
			}) as Schema,
	},
	"not equals": {
		operandType: f => (f === FieldType.NUMBER ? OperandType.NUMBER : OperandType.TEXT),
		fieldType: undefined,
		getValue: (s: Schema) => schemaConst(s.not),
		getSchema: (key, value) =>
			({
				type: "object",
				description: "not equals",
				properties: {
					[key]: {
						not: {
							const: value,
						},
					},
				},
			}) as Schema,
	},
	"is choice": {
		operandType: OperandType.CHOICE,
		fieldType: FieldType.CHOICE,
		choiceTypes: [ChoiceType.RADIO, ChoiceType.DROPDOWN],
		getValue: (s: Schema) => schemaConst(s),
		getSchema: (key, value) =>
			({
				type: "object",
				description: "is choice",
				required: [key],
				properties: {
					[key]: {
						type: "string",
						const: value,
					},
				},
			}) as Schema,
	},
	"is not choice": {
		operandType: OperandType.CHOICE,
		fieldType: FieldType.CHOICE,
		choiceTypes: [ChoiceType.RADIO, ChoiceType.DROPDOWN],
		getValue: (s: Schema) => schemaConst(s.not),
		getSchema: (key, value) =>
			({
				type: "object",
				description: "is not choice",
				properties: {
					[key]: {
						type: "string",
						not: {
							const: value,
						},
					},
				},
			}) as Schema,
	},
	"includes choice": {
		operandType: OperandType.CHOICE,
		fieldType: FieldType.CHOICE,
		choiceTypes: [ChoiceType.CHECKBOX],
		getValue: (s: Schema) => schemaConst(schemaFromContains(s.contains)),
		getSchema: (key, value) =>
			({
				type: "object",
				description: "includes choice",
				properties: {
					[key]: {
						type: "array",
						items: {
							type: "string",
						},
						contains: {
							const: value,
						},
					},
				},
			}) as Schema,
	},
	"does not include choice": {
		operandType: OperandType.CHOICE,
		fieldType: FieldType.CHOICE,
		choiceTypes: [ChoiceType.CHECKBOX],
		getValue: (s: Schema) => schemaConst(schemaFromContains(s.not?.contains)),
		getSchema: (key, value) =>
			({
				type: "object",
				description: "does not include choice",
				properties: {
					[key]: {
						type: "array",
						items: {
							type: "string",
						},
						not: {
							contains: {
								const: value,
							},
						},
					},
				},
			}) as Schema,
	},
	"greater than": {
		fieldType: FieldType.NUMBER,
		operandType: OperandType.NUMBER,
		getValue: (s: Schema) => (typeof s.exclusiveMinimum === "number" ? s.exclusiveMinimum : undefined),
		getSchema: (key, value) =>
			({
				type: "object",
				description: "greater than",
				required: [key],
				properties: {
					[key]: {
						type: "number",
						exclusiveMinimum: value,
					},
				},
			}) as Schema,
	},
	"greater than or equal": {
		fieldType: FieldType.NUMBER,
		operandType: OperandType.NUMBER,
		getValue: (s: Schema) => s.minimum,
		getSchema: (key, value) =>
			({
				type: "object",
				description: "greater than or equal",
				required: [key],
				properties: {
					[key]: {
						type: "number",
						minimum: value,
					},
				},
			}) as Schema,
	},
	"less than": {
		fieldType: FieldType.NUMBER,
		operandType: OperandType.NUMBER,
		getValue: (s: Schema) => s.maximum,
		getSchema: (key, value) =>
			({
				type: "object",
				description: "less than",
				required: [key],
				properties: {
					[key]: {
						type: "number",
						maximum: value,
					},
				},
			}) as Schema,
	},
	"less than or equal": {
		fieldType: FieldType.NUMBER,
		operandType: OperandType.NUMBER,
		getValue: (s: Schema) => (typeof s.exclusiveMaximum === "number" ? s.exclusiveMaximum : undefined),
		getSchema: (key, value) =>
			({
				type: "object",
				description: "less than or equal",
				required: [key],
				properties: {
					[key]: {
						type: "number",
						exclusiveMaximum: value,
					},
				},
			}) as Schema,
	},
	"is empty": {
		operandType: OperandType.EMPTY,
		getValue: undefined,
		fieldType: undefined,
		getSchema: (key, _) =>
			({
				type: "object",
				description: "is empty",
				properties: {
					[key]: {
						anyOf: [{ type: "string", maxLength: 0 }, { type: "number", not: {} }, { type: "null" }, { type: "array", maxItems: 0 }, { type: "object", maxProperties: 0 }],
					},
				},
			}) as Schema,
	},
	"is not empty": {
		operandType: OperandType.EMPTY,
		fieldType: undefined,
		getValue: undefined,
		getSchema: (key, _) =>
			({
				type: "object",
				description: "is not empty",
				required: [key],
				properties: {
					[key]: {
						anyOf: [{ type: "string", minLength: 1 }, { type: "number" }, { type: "array", minItems: 1 }, { type: "object", minProperties: 1 }],
					},
				},
			}) as Schema,
	},
	"is checked": {
		operandType: OperandType.EMPTY,
		getValue: undefined,
		fieldType: undefined,
		getSchema: (key, _) =>
			({
				type: "object",
				description: "is checked",
				properties: {
					[key]: { type: "boolean", const: true },
				},
			}) as Schema,
	},
	"is not checked": {
		operandType: OperandType.EMPTY,
		fieldType: undefined,
		getValue: undefined,
		getSchema: (key, _) =>
			({
				type: "object",
				description: "is not checked",
				properties: {
					[key]: { type: "boolean", const: false },
				},
			}) as Schema,
	},
	"matches regex": {
		operandType: OperandType.REGEX,
		fieldType: f => f !== FieldType.NUMBER,
		getValue: (s: Schema) => s.pattern,
		getSchema: (key, value) =>
			({
				type: "object",
				description: "matches regex",
				required: [key],
				properties: {
					[key]: {
						type: "string",
						pattern: value,
					},
				},
			}) as Schema,
	},
	contains: {
		operandType: OperandType.TEXT,
		fieldType: f => f !== FieldType.NUMBER,
		getValue: (s: Schema) => (s.pattern ? unescapeRegex(s.pattern) : undefined),
		getSchema: (key, value) =>
			({
				type: "object",
				description: "contains",
				required: [key],
				properties: {
					[key]: {
						type: "string",
						pattern: escapeRegex((value ?? "") as string),
					},
				},
			}) as Schema,
	},
} satisfies Record<string, OperatorOptions>;

export type Operator = keyof typeof operators;

const operatorTypes = Object.keys(operators) as Operator[];

// Distinguish operators that can be used with single checkbox fields and all others
export const baseOperators = operatorTypes.filter(op => op !== "is checked" && op !== "is not checked");
export const singleCheckboxOperators = ["is checked", "is not checked"] as const;

export type SchemaProducer = (key: string, value: string | number | undefined) => Schema;

export function parseSchema(s: Schema): [string, Operator, string | number | undefined] {
	if (!s.description) {
		return ["", operatorTypes[0], undefined];
	}
	const key = Object.keys(s.properties!)[0];
	const operator = s.description as Operator;
	const operand = operators[operator].getValue?.(s.properties![key]);
	return [key, operator, operand];
}
